import React, { useState, useEffect } from 'react';
import api from '../api';
import { CurrencyDollar, Receipt, Eye, Upload, CheckCircle, Clock } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { uploadFile } from '../utils/uploadFile';

const PrestadorFinanceiro = ({ prestador, isOpen, onClose }) => {
  const [relatorio, setRelatorio] = useState(null);
  const [payments, setPayments] = useState([]);
  const [mes, setMes] = useState('');
  const [loading, setLoading] = useState(true);
  const [paymentForm, setPaymentForm] = useState({ data: '', descricao: '', valor: '', solicitacao_id: '' });
  const [paymentFile, setPaymentFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && prestador) {
      const now = new Date();
      const m = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setMes(m);
      fetchData(m);
    }
  }, [isOpen, prestador]);

  const fetchData = async (month) => {
    setLoading(true);
    try {
      const [relRes, payRes] = await Promise.all([
        api.get(`/prestadores/${prestador.id}/relatorio-pagamento?mes=${month}`),
        api.get(`/pagamentos-prestadores?prestador_id=${prestador.id}`)
      ]);
      setRelatorio(relRes.data);
      setPayments(payRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleMesChange = (e) => {
    const m = e.target.value;
    setMes(m);
    fetchData(m);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let comprovante_url = null;
      if (paymentFile) {
        comprovante_url = await uploadFile(paymentFile, `pagamentos-prestadores/${prestador.id}`);
      }
      await api.post('/pagamentos-prestadores', {
        prestador_id: prestador.id,
        solicitacao_id: paymentForm.solicitacao_id || null,
        data: paymentForm.data,
        descricao: paymentForm.descricao,
        valor: parseFloat(paymentForm.valor),
        comprovante_url
      });
      setPaymentForm({ data: '', descricao: '', valor: '', solicitacao_id: '' });
      setPaymentFile(null);
      const payRes = await api.get(`/pagamentos-prestadores?prestador_id=${prestador.id}`);
      setPayments(payRes.data);
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    } finally { setSubmitting(false); }
  };

  const totalPago = payments.reduce((acc, p) => acc + (p.valor || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
            Financeiro - {prestador?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-zinc-400">Carregando...</div>
        ) : (
          <div className="space-y-6">
            {/* Month selector */}
            <div>
              <Label className="text-xs">Periodo</Label>
              <Input type="month" value={mes} onChange={handleMesChange} className="bg-[#050505] border-[#27272A] text-white w-48" data-testid="financeiro-mes-input" />
            </div>

            {/* Summary cards */}
            {relatorio && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm" data-testid="fin-atendimentos">
                  <div className="text-zinc-500 text-xs">Atendimentos</div>
                  <div className="text-2xl font-bold text-[#FFB800]">{relatorio.total_atendimentos}</div>
                </div>
                <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm" data-testid="fin-horas">
                  <div className="text-zinc-500 text-xs">Horas</div>
                  <div className="text-2xl font-bold text-[#FFB800]">{relatorio.total_horas.toFixed(1)}h</div>
                </div>
                <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm" data-testid="fin-a-receber">
                  <div className="text-zinc-500 text-xs">A Receber (periodo)</div>
                  <div className="text-2xl font-bold text-[#22C55E]">R$ {relatorio.total_a_receber.toFixed(2)}</div>
                </div>
                <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm" data-testid="fin-total-pago">
                  <div className="text-zinc-500 text-xs">Total Pago (geral)</div>
                  <div className="text-2xl font-bold text-white">R$ {totalPago.toFixed(2)}</div>
                </div>
              </div>
            )}

            {/* Detalhamento por atendimento */}
            {relatorio && relatorio.atendimentos.length > 0 && (
              <div className="border border-[#27272A] rounded-sm overflow-hidden">
                <div className="bg-[#050505] px-4 py-2 border-b border-[#27272A]">
                  <h4 className="text-[#FFB800] font-semibold text-sm">Atendimentos do Periodo</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#27272A] bg-[#050505]">
                        <th className="text-left text-zinc-400 py-2 px-3 text-xs">Solicitacao</th>
                        <th className="text-left text-zinc-400 py-2 px-3 text-xs">Data</th>
                        <th className="text-right text-zinc-400 py-2 px-3 text-xs">Horas</th>
                        <th className="text-right text-zinc-400 py-2 px-3 text-xs">Valor Horas</th>
                        <th className="text-right text-zinc-400 py-2 px-3 text-xs">Deslocamento</th>
                        <th className="text-right text-zinc-400 py-2 px-3 text-xs font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatorio.atendimentos.map((a, idx) => {
                        const valorHoras = (a.horas_executadas || 0) * relatorio.valor_hora;
                        const valorTotal = valorHoras + relatorio.taxa_deslocamento;
                        return (
                          <tr key={idx} className="border-b border-[#27272A]/50 hover:bg-[#1E1E1E]/50" data-testid={`fin-atendimento-${idx}`}>
                            <td className="py-2 px-3 text-[#FFB800] font-semibold">{a.numero_solicitacao}</td>
                            <td className="py-2 px-3 text-zinc-300">{a.data_conclusao ? new Date(a.data_conclusao).toLocaleDateString('pt-BR') : '-'}</td>
                            <td className="py-2 px-3 text-white text-right">{(a.horas_executadas || 0).toFixed(1)}h</td>
                            <td className="py-2 px-3 text-white text-right">R$ {valorHoras.toFixed(2)}</td>
                            <td className="py-2 px-3 text-white text-right">R$ {relatorio.taxa_deslocamento.toFixed(2)}</td>
                            <td className="py-2 px-3 text-[#22C55E] font-bold text-right">R$ {valorTotal.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-[#050505]">
                        <td colSpan={5} className="py-2 px-3 text-right text-zinc-400 font-bold">TOTAL:</td>
                        <td className="py-2 px-3 text-right text-[#22C55E] font-bold text-lg">R$ {relatorio.total_a_receber.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Registrar Pagamento */}
            <div className="border border-[#27272A] rounded-sm overflow-hidden">
              <div className="bg-[#050505] px-4 py-2 border-b border-[#27272A]">
                <h4 className="text-[#FFB800] font-semibold text-sm">Registrar Pagamento</h4>
              </div>
              <form onSubmit={handleAddPayment} className="p-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data *</Label>
                    <Input type="date" value={paymentForm.data} onChange={(e) => setPaymentForm({...paymentForm, data: e.target.value})} className="bg-[#050505] border-[#27272A] text-white text-sm" required data-testid="prest-payment-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Valor (R$) *</Label>
                    <Input type="number" step="0.01" value={paymentForm.valor} onChange={(e) => setPaymentForm({...paymentForm, valor: e.target.value})} className="bg-[#050505] border-[#27272A] text-white text-sm" required data-testid="prest-payment-valor" />
                  </div>
                  <div>
                    <Label className="text-xs">Comprovante</Label>
                    <Input type="file" accept="image/*,.pdf" onChange={(e) => setPaymentFile(e.target.files[0])} className="bg-[#050505] border-[#27272A] text-white text-sm" data-testid="prest-payment-comprovante" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Descricao *</Label>
                  <Input value={paymentForm.descricao} onChange={(e) => setPaymentForm({...paymentForm, descricao: e.target.value})} className="bg-[#050505] border-[#27272A] text-white text-sm" placeholder="Ex: Pagamento atendimento SOL-00001" required data-testid="prest-payment-descricao" />
                </div>
                <Button type="submit" className="bg-[#FFB800] text-black hover:bg-[#E6A600] text-sm" disabled={submitting} data-testid="prest-submit-payment">
                  {submitting ? 'Enviando...' : 'Registrar Pagamento'}
                </Button>
              </form>
            </div>

            {/* Historico de Pagamentos */}
            <div className="border border-[#27272A] rounded-sm overflow-hidden">
              <div className="bg-[#050505] px-4 py-2 border-b border-[#27272A]">
                <h4 className="text-[#FFB800] font-semibold text-sm">Historico de Pagamentos</h4>
              </div>
              {payments.length === 0 ? (
                <div className="p-4 text-zinc-500 text-center text-sm">Nenhum pagamento registrado</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#27272A] bg-[#050505]">
                        <th className="text-left text-zinc-400 py-2 px-3 text-xs">Data</th>
                        <th className="text-left text-zinc-400 py-2 px-3 text-xs">Descricao</th>
                        <th className="text-right text-zinc-400 py-2 px-3 text-xs">Valor</th>
                        <th className="text-center text-zinc-400 py-2 px-3 text-xs">Comprovante</th>
                        <th className="text-left text-zinc-400 py-2 px-3 text-xs">Registrado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id} className="border-b border-[#27272A]/50 hover:bg-[#1E1E1E]/50" data-testid={`prest-payment-row-${p.id}`}>
                          <td className="py-2 px-3 text-white">{new Date(p.data + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                          <td className="py-2 px-3 text-zinc-300">{p.descricao}</td>
                          <td className="py-2 px-3 text-[#22C55E] font-bold text-right">R$ {p.valor?.toFixed(2)}</td>
                          <td className="py-2 px-3 text-center">
                            {p.comprovante_url ? (
                              <a href={p.comprovante_url} target="_blank" rel="noopener noreferrer" className="text-[#FFB800] hover:text-[#E6A600] inline-flex items-center gap-1 text-xs"><Eye size={14} />Ver</a>
                            ) : <span className="text-zinc-600 text-xs">-</span>}
                          </td>
                          <td className="py-2 px-3 text-zinc-500 text-xs">{p.created_by || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrestadorFinanceiro;
